import React, { useCallback, useMemo, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'

import {
  InlineSelect,
  InlineSelectMenu,
  InlineSelectOption,
  InlineSelectTrigger,
} from 'controlled-components'
import {
  selectBottomOperator,
  selectLevel2Label,
  selectLevel2OperatorOptions,
  selectLevel2Subtitle,
  setBottomOperator,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Constants (fallback when no server config)
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_OPERATOR_OPTIONS = [
  { id: 'any', text: 'any of these', requiresValues: true },
  { id: 'all', text: 'all of these', requiresValues: true },
  { id: 'none', text: 'none of these', requiresValues: true },
  { id: 'empty', text: 'no', requiresValues: false },
  { id: 'skip', text: 'any', requiresValues: false },
]

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId?: number
  disabled?: boolean
}

type Props = OwnProps & ConnectedProps<typeof connector>

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

const OperatorContainer: React.FC<Props> = ({
  pickerId = 0,
  disabled = false,
  operator,
  serverOperatorOptions,
  subtitle,
  level2Label,
  setOperator,
}) => {
  const [css] = useStyletron()

  // Local UI state for dropdown open/close
  const [isOpen, setIsOpen] = useState(false)

  // Get operator options from global config or use defaults
  const operatorOptions = useMemo(
    () =>
      serverOperatorOptions.length > 0
        ? serverOperatorOptions
        : DEFAULT_OPERATOR_OPTIONS,
    [serverOperatorOptions]
  )

  // Get selected option label for display
  const selectedOption = operatorOptions.find(opt => opt.id === operator)
  const selectedLabel = selectedOption?.text ?? 'any of these'

  // Handlers
  const handleToggle = useCallback(() => {
    if (disabled) return
    setIsOpen(prev => !prev)
  }, [disabled])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleSelect = useCallback(
    (value: string) => {
      setOperator({ pickerId, operator: value })
      setIsOpen(false)
    },
    [setOperator, pickerId]
  )

  const containerClassName = css({
    whiteSpace: 'nowrap',
    flexShrink: 0,
  })

  // Build the operator dropdown element
  const operatorDropdown = (
    <InlineSelect isOpen={isOpen} onClose={handleClose}>
      <InlineSelectTrigger
        isOpen={isOpen}
        onToggle={handleToggle}
        disabled={disabled}
      >
        {selectedLabel}
      </InlineSelectTrigger>
      {!disabled && (
        <InlineSelectMenu isOpen={isOpen}>
          {operatorOptions.map(opt => (
            <InlineSelectOption
              key={opt.id}
              value={opt.id}
              selected={operator === opt.id}
              onSelect={handleSelect}
            >
              {opt.text}
            </InlineSelectOption>
          ))}
        </InlineSelectMenu>
      )}
    </InlineSelect>
  )

  // Render subtitle template with {operator} replaced by the dropdown
  // Falls back to "with {operator} {level2Label}" if no subtitle provided
  const renderContent = () => {
    const template = subtitle ?? `with {operator} ${level2Label.toLowerCase()}`
    const parts = template.split('{operator}')

    if (parts.length === 1) {
      // No {operator} placeholder - just show the subtitle as-is
      return <>{template}</>
    }

    return (
      <>
        {parts[0]}
        {operatorDropdown}
        {parts[1]}
      </>
    )
  }

  return (
    <Text.BodyText4 tag="span" color="grey70" className={containerClassName}>
      {renderContent()}
    </Text.BodyText4>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  operator: selectBottomOperator(state, ownProps.pickerId ?? 0),
  serverOperatorOptions: selectLevel2OperatorOptions(
    state,
    ownProps.pickerId ?? 0
  ),
  subtitle: selectLevel2Subtitle(state, ownProps.pickerId ?? 0),
  level2Label: selectLevel2Label(state, ownProps.pickerId ?? 0),
})

const mapDispatchToProps = {
  setOperator: setBottomOperator,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

OperatorContainer.displayName = 'OperatorContainer'

export default connector(OperatorContainer)
